import type { TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';
import { eq } from 'ember-truth-helpers';

import type {
  BarData,
  PlottedPoint,
  SymmetryData,
} from '../../types/components/prism/data-bar.ts';
import type PrismDataBarBlocksDataBarLabel from './data-bar/blocks/data-bar-label.gts';
import type PrismDataBarBlocksDataBarValue from './data-bar/blocks/data-bar-value.gts';
import PrismDataBarDataBarLabelValueTarget from './data-bar/data-bar-label-value-target.gts';
import PrismDataBarDataBarPlottedPoints from './data-bar/data-bar-plotted-points.gts';
import PrismDataBarDataBarSymmetricWithLabelAndValue from './data-bar/data-bar-symmetric-with-label-and-value.gts';

type DataBarType =
  | {
      data: BarData[];
      type?: 'basic';
    }
  | {
      data: SymmetryData;
      type: 'symmetric';
    }
  | {
      data: PlottedPoint[];
      type: 'plotted-points';
    };

interface PrismDataBarSignature {
  Args: DataBarType & {
    backgroundColor?: string;
    direction?: 'regular' | 'reverse';
    isAbsolute?: boolean;
    isGradient?: boolean;
    isStriped?: boolean;
    rounded?: 'left' | 'right' | 'both';
    targetValue?: number;
    // The percentage of the bar that will be filled with data
    thresholdWidthPercentage?: number;
    valueFormatter?: (value: number) => string;
    valuePosition?: 'top' | 'bottom';
  };
  Blocks: {
    default: [
      {
        label?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarLabel,
          'color' | 'text' | 'tooltip'
        >;
        value?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarValue,
          'color' | 'text' | 'tooltip' | 'valueItems'
        >;
      },
    ];
  };
  Element: HTMLDivElement;
}

const PrismDataBar: TOC<PrismDataBarSignature> = <template>
  {{#if (eq @type "plotted-points")}}
    <PrismDataBarDataBarPlottedPoints
      @data={{@data}}
      @isAbsolute={{@isAbsolute}}
      @valueFormatter={{@valueFormatter}}
    />
  {{else if (eq @type "symmetric")}}
    <PrismDataBarDataBarSymmetricWithLabelAndValue @data={{@data}} />
  {{else}}
    <PrismDataBarDataBarLabelValueTarget
      @backgroundColor={{@backgroundColor}}
      @data={{@data}}
      @direction={{@direction}}
      @isGradient={{@isGradient}}
      @isStriped={{@isStriped}}
      @rounded={{@rounded}}
      @targetValue={{@targetValue}}
      @thresholdWidthPercentage={{@thresholdWidthPercentage}}
      @valuePosition={{@valuePosition}}
      as |dataBarLabelValueTarget|
    >
      {{yield dataBarLabelValueTarget}}
    </PrismDataBarDataBarLabelValueTarget>
  {{/if}}
</template>;

export default PrismDataBar;

export type { BarData, PlottedPoint, SymmetryData };
